// Floating formatting toolbar anchored to the selected (or currently being
// edited) text-bearing shape.
//
// Why this component exists: text-bearing shapes (sticky, rect, ellipse,
// text shape, frame label) all carry an optional `textStyle` per the data
// model. The toolbar is the user-facing affordance for editing those
// properties without diving into the property panel.
//
// Anchoring: positioned in screen coords via the same viewport transform
// the textarea uses. The toolbar lives in the React tree alongside the
// textarea (mounted by Canvas), not as a child of the Stage, so it can
// be a normal DOM element and pick up Tailwind classes / Lucide icons.
//
// Don't-blur-the-textarea rule: clicking any button calls preventDefault
// on mousedown so focus stays on the textarea (when editing) and the
// editor doesn't dismiss on blur.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type * as Y from 'yjs';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Minus,
  Plus,
  Underline,
  Type as TypeIcon,
} from 'lucide-react';
import type { Shape } from '@frame/shared';
import { shapeBBox, updateShape } from './binding/shapesMap';
import { StickyColorButton } from './StickyColorButton';
import {
  clampFontSize,
  effectiveTextStyle,
  nextAlign,
  nextVerticalAlign,
  patchTextStyle,
  TEXT_COLOR_PRESETS,
  textStyleSupports,
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
} from './textStyle';
import { useTokens } from '../styles/theme';

interface Props {
  doc: Y.Doc;
  shape: Shape;
  viewport: { x: number; y: number; scale: number };
  containerSize: { width: number; height: number };
  editing: boolean;
}

const TOOLBAR_GAP = 8; // px between toolbar and shape bbox
const TOOLBAR_HEIGHT_APPROX = 36;
// Fallback used for the very first paint, before useLayoutEffect measures
// the real toolbar width. After that, the measured value drives clamping.
const TOOLBAR_WIDTH_FALLBACK = 320;

// Which popover, if any, is currently open inside the toolbar. Lifted out of
// the individual picker components so only one can be open at a time and so
// outside-click logic uses a single source of truth.
type OpenPopover = 'text' | 'sticky' | null;

export function TextFormatToolbar({ doc, shape, viewport, containerSize, editing }: Props) {
  const tokens = useTokens();
  const style = effectiveTextStyle(shape, tokens);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [openPopover, setOpenPopover] = useState<OpenPopover>(null);
  const [toolbarWidth, setToolbarWidth] = useState(TOOLBAR_WIDTH_FALLBACK);

  // Keyboard shortcuts — Cmd/Ctrl + B/I/U toggle bold/italic/underline.
  // Active when the textarea is focused OR (when not editing) the
  // text-bearing shape is the sole selection. We attach to window so the
  // shortcut fires even when focus is on the textarea, and we let other
  // Cmd combos through untouched (undo, select-all, etc.).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta || e.shiftKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== 'b' && key !== 'i' && key !== 'u') return;
      // When not editing, only fire if focus isn't on some other form field
      // (e.g. the InvitePanel input). The textarea case is fine because
      // editing === true.
      if (!editing) {
        const target = e.target as HTMLElement | null;
        if (target && isFormField(target) && target.tagName !== 'TEXTAREA') return;
      }
      const field: 'bold' | 'italic' | 'underline' =
        key === 'b' ? 'bold' : key === 'i' ? 'italic' : 'underline';
      if (!textStyleSupports(shape, field)) return;
      e.preventDefault();
      const next = !style[field];
      updateShape(doc, shape.id, { textStyle: patchTextStyle(shape, field, next) });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, shape, style, editing]);

  // Measure the toolbar after layout so clamping uses the real width rather
  // than an estimate. Re-measure whenever the shape changes (different shape
  // types render different button sets, e.g. sticky adds the paper-color
  // button) or when the popover state changes (popovers extend the bbox in
  // height but not in width, so this is mostly belt-and-braces).
  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    setToolbarWidth(el.offsetWidth);
  }, [shape.type, openPopover]);

  const bbox = shapeBBox(shape);
  if (!bbox) return null;

  // Project bbox to screen coords.
  const screenLeft = viewport.x + bbox.x * viewport.scale;
  const screenTop = viewport.y + bbox.y * viewport.scale;
  const screenRight = screenLeft + bbox.w * viewport.scale;
  const screenWidth = screenRight - screenLeft;

  // Anchor centered horizontally above the shape; flip below if the toolbar
  // would clip the top of the viewport. When flipped below, popovers should
  // open upward instead of downward so they don't clip the viewport bottom.
  const idealTop = screenTop - TOOLBAR_HEIGHT_APPROX - TOOLBAR_GAP;
  const flipBelow = idealTop < TOOLBAR_GAP;
  const top = flipBelow
    ? screenTop + bbox.h * viewport.scale + TOOLBAR_GAP
    : idealTop;
  const centeredLeft = screenLeft + screenWidth / 2 - toolbarWidth / 2;
  const clampedLeft = Math.max(
    TOOLBAR_GAP,
    Math.min(containerSize.width - toolbarWidth - TOOLBAR_GAP, centeredLeft),
  );

  // Field updaters — each writes the patched textStyle via updateShape with
  // the default LOCAL_ORIGIN_EDIT so formatting coalesces with typing in the
  // undo stack.
  const setFontSize = (n: number) => {
    updateShape(doc, shape.id, {
      textStyle: patchTextStyle(shape, 'fontSize', clampFontSize(n)),
    });
  };
  const toggle = (field: 'bold' | 'italic' | 'underline') => {
    updateShape(doc, shape.id, {
      textStyle: patchTextStyle(shape, field, !style[field]),
    });
  };
  const setColor = (color: string) => {
    updateShape(doc, shape.id, {
      textStyle: patchTextStyle(shape, 'color', color),
    });
    setOpenPopover(null);
  };
  const cycleAlign = () => {
    updateShape(doc, shape.id, {
      textStyle: patchTextStyle(shape, 'align', nextAlign(style.align)),
    });
  };
  const cycleVAlign = () => {
    updateShape(doc, shape.id, {
      textStyle: patchTextStyle(shape, 'verticalAlign', nextVerticalAlign(style.verticalAlign)),
    });
  };

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label="Text formatting"
      style={{
        position: 'fixed',
        left: clampedLeft,
        top,
        zIndex: 10,
      }}
      className="
        flex items-center gap-1 p-1
        bg-bg-panel border border-border-default rounded-3 shadow-md
        text-fg-secondary
      "
    >
      <FontSizeStepper value={style.fontSize} onChange={setFontSize} />

      <Divider />

      {textStyleSupports(shape, 'bold') && (
        <ToggleButton active={style.bold} onClick={() => toggle('bold')} label="Bold (Cmd+B)">
          <Bold size={14} strokeWidth={1.75} />
        </ToggleButton>
      )}
      {textStyleSupports(shape, 'italic') && (
        <ToggleButton active={style.italic} onClick={() => toggle('italic')} label="Italic (Cmd+I)">
          <Italic size={14} strokeWidth={1.75} />
        </ToggleButton>
      )}
      {textStyleSupports(shape, 'underline') && (
        <ToggleButton
          active={style.underline}
          onClick={() => toggle('underline')}
          label="Underline (Cmd+U)"
        >
          <Underline size={14} strokeWidth={1.75} />
        </ToggleButton>
      )}

      {textStyleSupports(shape, 'color') && (
        <>
          <Divider />
          <ColorButton
            color={style.color}
            open={openPopover === 'text'}
            onToggle={() => setOpenPopover((o) => (o === 'text' ? null : 'text'))}
            onClose={() => setOpenPopover(null)}
            onPick={setColor}
            currentColor={style.color}
            flipUp={flipBelow}
          />
        </>
      )}

      {shape.type === 'sticky' && (
        <>
          <Divider />
          <StickyColorButton
            shape={shape}
            palette={tokens.sticky}
            doc={doc}
            open={openPopover === 'sticky'}
            onToggle={() => setOpenPopover((o) => (o === 'sticky' ? null : 'sticky'))}
            onClose={() => setOpenPopover(null)}
            flipUp={flipBelow}
          />
        </>
      )}

      <Divider />

      {textStyleSupports(shape, 'align') && (
        <CycleButton onClick={cycleAlign} label={`Align ${style.align}`}>
          {style.align === 'left' && <AlignLeft size={14} strokeWidth={1.75} />}
          {style.align === 'center' && <AlignCenter size={14} strokeWidth={1.75} />}
          {style.align === 'right' && <AlignRight size={14} strokeWidth={1.75} />}
        </CycleButton>
      )}
      {textStyleSupports(shape, 'verticalAlign') && (
        <CycleButton onClick={cycleVAlign} label={`Vertical align ${style.verticalAlign}`}>
          <VerticalAlignIcon valign={style.verticalAlign} />
        </CycleButton>
      )}
    </div>
  );
}

function Divider() {
  return <div aria-hidden className="w-px h-5 bg-border-subtle mx-0.5" />;
}

// Shared button shell for the toolbar icons. ToggleButton stamps `aria-pressed`
// so screen readers announce the on/off state; CycleButton omits it because
// cycle actions rotate through > 2 states and aren't toggles.
const BUTTON_CLASSES = [
  'h-7 w-7 inline-flex items-center justify-center rounded-2',
  'transition-colors duration-micro ease-out',
  'focus-visible:outline-none focus-visible:shadow-focus',
].join(' ');

function ToggleButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      // Prevent focus from leaving the textarea when editing — preventDefault
      // on mousedown stops the browser's default focus shift.
      onMouseDown={(e) => e.preventDefault()}
      className={[
        BUTTON_CLASSES,
        active
          ? 'bg-bg-active text-fg-primary'
          : 'text-fg-secondary hover:bg-bg-hover hover:text-fg-primary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function CycleButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      className={[
        BUTTON_CLASSES,
        'text-fg-secondary hover:bg-bg-hover hover:text-fg-primary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function FontSizeStepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [draft, setDraft] = useState(String(value));

  // Keep input in sync if value updates externally (peer write, shortcut).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const n = parseInt(draft, 10);
    if (Number.isFinite(n)) onChange(n);
    else setDraft(String(value));
  };

  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        title="Decrease font size"
        aria-label="Decrease font size"
        onClick={() => onChange(value - 1)}
        onMouseDown={(e) => e.preventDefault()}
        disabled={value <= MIN_FONT_SIZE}
        className="
          h-7 w-7 inline-flex items-center justify-center rounded-2
          text-fg-secondary hover:bg-bg-hover hover:text-fg-primary
          transition-colors duration-micro ease-out
          disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-secondary
          focus-visible:outline-none focus-visible:shadow-focus
        "
      >
        <Minus size={14} strokeWidth={1.75} />
      </button>
      <div className="flex items-center gap-1 px-1">
        <TypeIcon size={13} strokeWidth={1.75} className="text-fg-tertiary" />
        <input
          type="number"
          min={MIN_FONT_SIZE}
          max={MAX_FONT_SIZE}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="
            w-10 h-7 px-1
            bg-transparent text-13 text-fg-primary text-center tabular-nums
            font-mono
            focus:outline-none
            [appearance:textfield]
            [&::-webkit-outer-spin-button]:appearance-none
            [&::-webkit-inner-spin-button]:appearance-none
          "
        />
      </div>
      <button
        type="button"
        title="Increase font size"
        aria-label="Increase font size"
        onClick={() => onChange(value + 1)}
        onMouseDown={(e) => e.preventDefault()}
        disabled={value >= MAX_FONT_SIZE}
        className="
          h-7 w-7 inline-flex items-center justify-center rounded-2
          text-fg-secondary hover:bg-bg-hover hover:text-fg-primary
          transition-colors duration-micro ease-out
          disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-secondary
          focus-visible:outline-none focus-visible:shadow-focus
        "
      >
        <Plus size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
}

// Hex matches CSS color literals only: 3, 4 (RGBA), 6, or 8 (RRGGBBAA) hex
// chars after `#`. Looser ranges (e.g. {3,8}) accept invalid 5/7-char inputs.
const HEX_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function ColorButton({
  color,
  open,
  onToggle,
  onClose,
  onPick,
  currentColor,
  flipUp,
}: {
  color: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onPick: (color: string) => void;
  currentColor: string;
  flipUp: boolean;
}) {
  const [hex, setHex] = useState(color);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  const firstSwatchRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setHex(color);
  }, [color]);

  // Outside-click + Escape close the popover. The wrapRef contains the
  // trigger AND the popover, so clicks inside either don't close (the hex
  // input needs to stay focused while the user types).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => firstSwatchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const commitHex = () => {
    const v = hex.trim();
    if (HEX_RE.test(v)) {
      onPick(v);
    } else {
      setHex(color);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        title="Text color"
        aria-label="Text color"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={onToggle}
        onMouseDown={(e) => e.preventDefault()}
        className="
          h-7 w-7 inline-flex items-center justify-center rounded-2
          hover:bg-bg-hover transition-colors duration-micro ease-out
          focus-visible:outline-none focus-visible:shadow-focus
        "
      >
        <span
          aria-hidden
          className="w-4 h-4 rounded-full border border-border-default"
          style={{ backgroundColor: color }}
        />
      </button>
      {open && (
        <div
          id={popoverId}
          role="dialog"
          aria-label="Text color"
          className={[
            'absolute left-1/2 -translate-x-1/2 w-fit p-2',
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1',
            'bg-bg-panel border border-border-default rounded-3 shadow-md',
            'flex flex-col gap-2',
          ].join(' ')}
        >
          {/*
            Explicit column sizing — Tailwind's bare `grid-cols-6` collapses
            to 0px-wide columns inside an auto-width absolute container.
            See StickyColorButton for the longer story (commit 744f094).
          */}
          <div className="grid grid-cols-[repeat(6,1.5rem)] gap-1.5">
            {TEXT_COLOR_PRESETS.map((preset, i) => {
              const active = preset.toLowerCase() === currentColor.toLowerCase();
              return (
                <button
                  key={preset}
                  ref={i === 0 ? firstSwatchRef : undefined}
                  type="button"
                  aria-label={preset}
                  aria-pressed={active}
                  onClick={() => onPick(preset)}
                  onMouseDown={(e) => e.preventDefault()}
                  className={[
                    'w-6 h-6 rounded-full border-2',
                    'transition-transform duration-micro hover:scale-110',
                    'focus-visible:outline-none focus-visible:shadow-focus',
                  ].join(' ')}
                  style={{
                    backgroundColor: preset,
                    borderColor: active ? 'var(--accent)' : 'transparent',
                  }}
                />
              );
            })}
          </div>
          <input
            type="text"
            value={hex}
            onChange={(e) => setHex(e.target.value)}
            onBlur={commitHex}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHex();
              }
            }}
            placeholder="#000000"
            className="
              h-7 px-2
              bg-bg-app border border-border-default rounded-2
              text-12 font-mono text-fg-primary
              focus:outline-none focus:border-accent
            "
          />
        </div>
      )}
    </div>
  );
}

// Lucide doesn't ship dedicated valign icons, so we draw a minimal SVG that
// shifts the "bar" between top/middle/bottom slots. Keeps the toolbar
// visually consistent with the lucide icons in adjacent buttons.
function VerticalAlignIcon({ valign }: { valign: 'top' | 'middle' | 'bottom' }) {
  const barY = valign === 'top' ? 4 : valign === 'middle' ? 7 : 10;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="2.5" y="2.5" width="9" height="9" rx="1.25" stroke="currentColor" strokeWidth="1.25" />
      <rect x="4.5" y={barY} width="5" height="1.4" rx="0.5" fill="currentColor" />
    </svg>
  );
}

function isFormField(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'INPUT') return true;
  return el.isContentEditable;
}
